<template>
  <div class="ccm" role="dialog" aria-modal="true" aria-labelledby="coverage-title" aria-describedby="coverage-subtitle">
    <div class="ccm__backdrop" @click="$emit('close')"></div>

    <section class="ccm__panel">
      <header class="ccm__header">
        <div class="ccm__header-copy">
          <span class="ccm__kicker">
            <i class="fa fa-chart-bar" aria-hidden="true"></i>
            BarbershopTalk Intelligence
          </span>
          <h2 id="coverage-title" class="ccm__title">How Different Media Cover This Story</h2>
          <p id="coverage-subtitle" class="ccm__subtitle">{{ subtitle }}</p>
        </div>

        <button class="ccm__close" type="button" aria-label="Close coverage comparison" @click="$emit('close')">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div v-if="loading" class="ccm__state ccm__state--loading" aria-live="polite" aria-label="Loading coverage comparison">
        <div class="ccm__skeleton ccm__skeleton--title"></div>
        <div class="ccm__skeleton-grid">
          <span class="ccm__skeleton"></span>
          <span class="ccm__skeleton"></span>
          <span class="ccm__skeleton"></span>
        </div>
        <p class="ccm__loading-hint">
          <i class="fa fa-circle-notch fa-spin" aria-hidden="true"></i>
          Scanning coverage across sources...
        </p>
      </div>

      <div v-else-if="error" class="ccm__state ccm__state--error" aria-live="assertive">
        <span class="ccm__state-icon" aria-hidden="true">
          <i class="fa fa-exclamation-triangle"></i>
        </span>
        <h3>Comparison unavailable right now.</h3>
        <p>{{ error }}</p>
        <button class="ccm__button ccm__button--primary" type="button" @click="fetchCoverage">
          <i class="fa fa-redo" aria-hidden="true"></i>
          Try again
        </button>
      </div>

      <div v-else class="ccm__body">
        <section class="ccm__hero" :class="{ 'ccm__hero--singleton': isSingleton }">
          <div class="ccm__hero-copy">
            <div class="ccm__hero-stat" :aria-label="`${sourceCount} sources covered this story`">
              <span class="ccm__metric">{{ sourceCount }}</span>
              <span class="ccm__metric-label">sources</span>
            </div>
            <div>
              <h3>{{ heroTitle }}</h3>
              <p>{{ heroCopy }}</p>
            </div>
          </div>

          <EmotionalGapBadge
            v-if="isPolitical"
            class="ccm__hero-gap"
            :gap="coverage.emotionalGap"
            compact
          />
        </section>

        <section v-if="isSingleton" class="ccm__empty" aria-label="No coverage comparison available">
          <span class="ccm__empty-icon" aria-hidden="true">
            <i class="fa fa-satellite-dish"></i>
          </span>
          <h3>Tracking coverage now.</h3>
          <p>
            This story hasn't matched with other sources yet. It may be breaking news,
            niche coverage, or simply ahead of the cycle.
            Check back soon.
          </p>
          <p v-if="displayTitle" class="ccm__empty-title">
            <i class="fa fa-file-alt" aria-hidden="true"></i>
            {{ displayTitle }}
          </p>
        </section>

        <template v-else>
          <section v-if="isPolitical" class="ccm__intelligence">
            <div class="ccm__claim">
              <span class="ccm__claim-label">
                <i class="fa fa-bolt" aria-hidden="true"></i>
                Emotional X-ray
              </span>
              <p>{{ politicalClaim }}</p>
              <p class="ccm__claim-explainer">
                Emotional gap measures how differently left and right sources frame the same story.
                A higher number means more divergence in tone, and less shared reality.
              </p>
            </div>

            <PerspectiveBar
              :bias-breakdown="coverage.biasBreakdown || {}"
              :perspective-stats="coverage.perspectiveStats || null"
            />

            <EmotionalGapBadge :gap="coverage.emotionalGap" />

            <a
              v-if="oppositePerspectivePath"
              class="ccm__opposite-cta"
              :href="oppositePerspectivePath"
              @click.prevent="navigateTo(oppositePerspectivePath)"
              :aria-label="`Read this story from the opposite perspective`"
            >
              <i class="fa fa-exchange-alt" aria-hidden="true"></i>
              Read how the other side covers this
            </a>
          </section>

          <section v-else class="ccm__pulse">
            <div class="ccm__pulse-header">
              <div>
                <span class="ccm__claim-label">Story Pulse</span>
                <h3>{{ pulseTitle }}</h3>
              </div>
              <div v-if="avgSentimentAcrossAll !== null" class="ccm__pulse-score" :aria-label="`Average sentiment ${avgSentimentAcrossAll} out of 100`">
                <span class="ccm__pulse-emoji" aria-hidden="true">{{ getSentimentEmoji(avgSentimentAcrossAll) }}</span>
                <div>
                  <strong class="ccm__pulse-value">{{ avgSentimentAcrossAll }}/100</strong>
                  <span class="ccm__pulse-label">avg tone</span>
                </div>
              </div>
            </div>
            <p v-if="sentimentNarrative" class="ccm__pulse-narrative">{{ sentimentNarrative }}</p>
          </section>

          <div v-if="isAnonymous" class="ccm__upsell" role="complementary" aria-label="Sign up to unlock more features">
            <p>
              <strong>Get the Daily Cut free.</strong> Turn this comparison into a daily briefing:
              follow topics, track story momentum, get blind spot alerts. Published Under Sniper picks are on Limited and Full.
            </p>
            <a href="/register" class="ccm__button ccm__button--upsell">
              Create free account
            </a>
          </div>

          <section class="ccm__coverage">
            <div class="ccm__section-heading">
              <div>
                <span class="ccm__kicker">{{ isPolitical ? 'Perspective Evidence' : 'All Coverage' }}</span>
                <h3>{{ isPolitical ? 'Other Coverage' : 'All Sources' }}</h3>
              </div>
              <span class="ccm__count">{{ sortedCoverage.length }} {{ sortedCoverage.length === 1 ? 'source' : 'sources' }}</span>
            </div>

            <div class="ccm__article-list">
              <article v-for="article in sortedCoverage" :key="article.id || article.urlPath" class="ccm__article">
                <a
                  class="ccm__article-link"
                  :href="article.urlPath || '#'"
                  :aria-label="`Open coverage from ${article.sourceName || 'another source'}`"
                  @click="handleArticleClick($event, article)"
                >
                  <img
                    v-if="article.imageUrl"
                    class="ccm__article-image"
                    :src="article.imageUrl"
                    :alt="article.title || 'Coverage article image'"
                  >
                  <div v-else class="ccm__article-image ccm__article-image--empty" aria-hidden="true">
                    <i class="fa fa-newspaper"></i>
                  </div>

                  <div class="ccm__article-copy">
                    <div class="ccm__article-meta">
                      <span>{{ article.sourceName || 'Unknown source' }}</span>
                      <span v-if="formatPublished(article.publishedAt)">{{ formatPublished(article.publishedAt) }}</span>
                    </div>
                    <h4>{{ article.title || 'Untitled coverage' }}</h4>
                    <div class="ccm__article-tags">
                      <span
                        v-if="isPolitical && sourceCohortLabel(article)"
                        class="ccm__tag ccm__tag--source"
                        :title="sourceMixTooltip"
                        :aria-label="`Source: ${sourceCohortLabel(article)} cohort`"
                      >
                        Source: {{ sourceCohortLabel(article) }}
                      </span>
                      <span
                        v-if="isPolitical && framingBadgeLabel(article)"
                        class="ccm__tag ccm__tag--framing"
                        :class="{ 'ccm__tag--insufficient': isArticleFramingAbstained(article) }"
                        :title="framingBadgeTitle(article)"
                        tabindex="0"
                        :aria-label="`Framing: ${framingBadgeLabel(article)}`"
                      >
                        Framing: {{ framingBadgeLabel(article) }}
                      </span>
                      <span
                        v-if="hasSentiment(article.sentimentScore)"
                        class="ccm__tag ccm__tag--sentiment"
                        :title="sentimentTooltip"
                        :aria-label="`Tone: ${Math.round(Number(article.sentimentScore))} of 100`"
                      >
                        <span aria-hidden="true">{{ getSentimentEmoji(article.sentimentScore) }}</span>
                        Tone: {{ Math.round(Number(article.sentimentScore)) }}/100
                      </span>
                    </div>
                  </div>
                </a>
              </article>
            </div>
          </section>
        </template>
      </div>

      <footer class="ccm__footer">
        <p class="ccm__footer-brand">
          <i class="fa fa-shield-alt" aria-hidden="true"></i>
          Powered by BarbershopTalk Intelligence
        </p>
        <div class="ccm__footer-actions">
          <button class="ccm__button ccm__button--ghost" type="button" @click="$emit('close')">
            Close
          </button>
          <a
            v-if="!loading && !error && !isSingleton"
            class="ccm__button ccm__button--share"
            :href="tweetUrl"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Share this coverage comparison on X"
          >
            <span class="ccm__x-logo" aria-hidden="true">𝕏</span>
            Share
          </a>
          <a
            v-if="primaryCoveragePath"
            class="ccm__button ccm__button--primary"
            :href="primaryCoveragePath"
            @click.prevent="navigateTo(primaryCoveragePath)"
          >
            {{ isPolitical ? 'Read from a different perspective' : 'Read from another outlet' }}
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </a>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import ArticleDataService from '@/services/ArticleDataService'
import EmotionalGapBadge from '@/components/EmotionalGapBadge.vue'
import PerspectiveBar from '@/components/PerspectiveBar.vue'
import { getPerspectiveLabel, getSentimentEmoji } from '@/utils/coverageHelpers'
import { onFeatureEvent } from '@/composables/useTutorialProgress'
import { buildPublicUrl } from '@/utils/publicUrl'
import {
  FRAMING_TOOLTIP,
  INSUFFICIENT_TOOLTIP,
  SENTIMENT_TOOLTIP,
  SOURCE_MIX_TOOLTIP,
  getFramingDisplayLabel,
  hasFramingFields,
  isFramingAbstained,
} from '@/utils/framingHelpers'

function toFiniteNumber(value) {
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export default {
  name: 'CompareCoverageModal',

  components: {
    EmotionalGapBadge,
    PerspectiveBar,
  },

  props: {
    articleId: {
      type: Number,
      default: null,
    },
    clusterId: {
      type: String,
      default: null,
    },
    articleTitle: {
      type: String,
      default: '',
    },
  },

  emits: ['close'],

  data() {
    return {
      loading: true,
      error: null,
      coverage: null,
      sourceMixTooltip: SOURCE_MIX_TOOLTIP,
      sentimentTooltip: SENTIMENT_TOOLTIP,
    }
  },

  computed: {
    otherCoverage() {
      return Array.isArray(this.coverage?.otherCoverage) ? this.coverage.otherCoverage : []
    },

    sortedCoverage() {
      if (!this.isPolitical || this.userBiasView === 'mixed') return this.otherCoverage

      const preferredCategories = {
        left: ['left_wing', 'leaning_left_wing'],
        right: ['right_wing', 'leaning_right_wing'],
        center: ['center'],
      }[this.userBiasView]

      if (!preferredCategories) return this.otherCoverage

      const preferred = []
      const rest = []
      this.otherCoverage.forEach(article => {
        if (preferredCategories.includes(article?.categorySpecified)) {
          preferred.push(article)
        } else {
          rest.push(article)
        }
      })

      return [...preferred, ...rest]
    },

    sourceCount() {
      return this.otherCoverage.length + (this.coverage?.thisArticle ? 1 : 0)
    },

    isPolitical() {
      return this.coverage?.biasMode === 'political'
    },

    isSingleton() {
      return !this.coverage?.clusterId || this.otherCoverage.length === 0
    },

    displayTitle() {
      return this.coverage?.thisArticle?.title || this.articleTitle
    },

    normalizedGap() {
      const gap = toFiniteNumber(this.coverage?.emotionalGap)
      return gap === null || gap < 0 ? null : Math.round(gap)
    },

    subtitle() {
      if (this.loading) return 'Scanning the spread across matching sources.'
      if (this.error) return 'The comparison could not load yet.'
      if (this.isSingleton) return 'No other sources found yet.'
      if (this.isPolitical) {
        const gapText = this.normalizedGap === null ? 'emotional gap pending' : `${this.normalizedGap}-point emotional gap`
        return `${this.sourceCount} sources - ${gapText}`
      }
      return `${this.sourceCount} sources covering this story`
    },

    heroTitle() {
      if (this.isSingleton) return 'No comparison available yet'
      if (this.isPolitical) return 'This story is splitting emotional framing'
      const cat = (this.coverage?.thisArticle?.category || '').toLowerCase()
      const catSpec = (this.coverage?.thisArticle?.categorySpecified || '').toLowerCase()
      const isSports = ['sports', 'nfl', 'nba', 'nhl', 'mma'].includes(cat) || ['nfl', 'nba', 'nhl', 'mma'].includes(catSpec)
      const isBusiness = cat === 'business' || catSpec === 'business'
      const isTechnology = cat === 'technology' || catSpec === 'technology'
      if (isSports) return 'Sports media is tracking this story'
      if (isBusiness) return 'Business outlets are covering this story'
      if (isTechnology) return 'Tech outlets are covering this story'
      return 'Multiple sources are covering this story'
    },

    heroCopy() {
      if (this.isSingleton) {
        return 'We found the article, but not enough matching coverage to compare yet.'
      }
      if (this.isPolitical) {
        return 'Bias labels show who covered it. Emotional gap shows how differently audiences are being framed to feel.'
      }
      return 'See how the story is being framed across ' + this.sourceCount + ' outlets and whether the tone is consistent.'
    },

    avgSentimentAcrossAll() {
      const scores = this.otherCoverage
        .map(a => Number(a.sentimentScore))
        .filter(n => Number.isFinite(n))
      if (scores.length === 0) return null
      return Math.round(scores.reduce((sum, n) => sum + n, 0) / scores.length)
    },

    sentimentNarrative() {
      const avg = this.avgSentimentAcrossAll
      const total = this.otherCoverage.length
      if (avg === null || total === 0) return null
      if (avg <= 20) return `All ${total} outlet${total === 1 ? '' : 's'} are framing this story negatively. The emotional tone is bleak across the board.`
      if (avg <= 40) return `Coverage is mostly negative across ${total} source${total === 1 ? '' : 's'}. The story is being framed with concern or criticism.`
      if (avg <= 60) return `The tone is mixed across ${total} source${total === 1 ? '' : 's'}. No clear emotional consensus from the media.`
      if (avg <= 80) return `Coverage is largely positive across ${total} source${total === 1 ? '' : 's'}. The story is being framed favorably.`
      return `The media is overwhelmingly positive on this. All ${total} source${total === 1 ? '' : 's'} are framing it favorably.`
    },

    pulseTitle() {
      const avg = this.avgSentimentAcrossAll
      if (avg === null) return 'Media coverage tracked'
      if (avg <= 20) return 'Overwhelmingly negative coverage'
      if (avg <= 40) return 'Mostly negative coverage'
      if (avg <= 60) return 'Mixed tone across outlets'
      if (avg <= 80) return 'Mostly positive coverage'
      return 'Overwhelmingly positive coverage'
    },

    leftAverage() {
      return toFiniteNumber(this.coverage?.perspectiveStats?.left?.avgSentiment)
    },

    rightAverage() {
      return toFiniteNumber(this.coverage?.perspectiveStats?.right?.avgSentiment)
    },

    politicalClaim() {
      if (this.leftAverage !== null && this.rightAverage !== null) {
        return `Left readers see this at ${Math.round(this.leftAverage)}/100. Right readers see it at ${Math.round(this.rightAverage)}/100. That difference is the story behind the story.`
      }

      return 'This cluster has political coverage, but sentiment averages are still incomplete for one or more perspectives.'
    },

    primaryCoveragePath() {
      return this.sortedCoverage.find(article => article?.urlPath)?.urlPath || null
    },

    tweetUrl() {
      const title = this.coverage?.thisArticle?.title || this.articleTitle || 'this story'
      const articlePath = this.coverage?.thisArticle?.urlPath || this.$route?.fullPath || '';
      const articleUrl = buildPublicUrl(articlePath) || '';
      let text
      if (this.isPolitical && this.normalizedGap !== null) {
        text = `Left readers see this at ${Math.round(this.leftAverage ?? 0)}/100. Right readers at ${Math.round(this.rightAverage ?? 0)}/100. That's a ${this.normalizedGap}-point emotional gap, and Ground News won't tell you that.`
      } else if (this.avgSentimentAcrossAll !== null) {
        text = `${this.sourceCount} outlets covered "${title.slice(0, 60)}". The media tone is ${this.avgSentimentAcrossAll}/100. See the full spread on BarbershopTalk.`
      } else {
        text = `${this.sourceCount} outlets covered "${title.slice(0, 60)}". See the full media spread on BarbershopTalk.`
      }
      return `https://x.com/intent/post?text=${encodeURIComponent(text)}&url=${encodeURIComponent(articleUrl)}`
    },

    oppositePerspectivePath() {
      if (!this.isPolitical || !this.otherCoverage.length) return null
      const currentCategory = this.coverage?.thisArticle?.categorySpecified || ''
      const isLeft = ['left_wing', 'leaning_left_wing'].includes(currentCategory)
      const isRight = ['right_wing', 'leaning_right_wing'].includes(currentCategory)
      if (!isLeft && !isRight) return null
      const targetSide = isLeft
        ? ['right_wing', 'leaning_right_wing']
        : ['left_wing', 'leaning_left_wing']
      const opposite = this.otherCoverage.find(a =>
        targetSide.includes(a.categorySpecified) && a.urlPath
      )
      return opposite?.urlPath || null
    },

    ...mapState({
      currentUserId: state => state.user?.id,
      currentDefaultBiasView: state => state.user?.preferences?.defaultBiasView,
    }),

    userBiasView() {
      return this.currentDefaultBiasView || 'mixed'
    },

    isAnonymous() {
      return !this.currentUserId
    },
  },

  mounted() {
    this.fetchCoverage()
    document.addEventListener('keydown', this.handleKeydown)
    this.focusFirstElement()
    onFeatureEvent('compare-coverage')
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown)
  },

  watch: {
    articleId() {
      this.fetchCoverage()
    },
    clusterId() {
      this.fetchCoverage()
    },
  },

  methods: {
    getPerspectiveLabel,
    getSentimentEmoji,

    sourceCohortLabel(article) {
      if (!article?.categorySpecified) return null
      const label = getPerspectiveLabel(article.categorySpecified)
      if (!label || label === 'Unknown Perspective') return null
      return label
    },

    framingBadgeLabel(article) {
      if (!hasFramingFields(article)) return null
      return getFramingDisplayLabel(article)
    },

    isArticleFramingAbstained(article) {
      return isFramingAbstained(article)
    },

    framingBadgeTitle(article) {
      if (isFramingAbstained(article)) return INSUFFICIENT_TOOLTIP
      return FRAMING_TOOLTIP
    },

    async fetchCoverage() {
      if (!this.articleId && !this.clusterId) {
        this.loading = false
        this.error = 'Missing story id for this comparison.'
        this.coverage = null
        return
      }

      this.loading = true
      this.error = null

      try {
        const response = this.clusterId
          ? await ArticleDataService.getStoryClusterDetail(this.clusterId)
          : await ArticleDataService.getCoverageSpread(this.articleId)
        this.coverage = response.data || null
      } catch (error) {
        console.error('Failed to load coverage spread:', error)
        this.coverage = null
        this.error = 'Try again in a moment. If this story was just published, comparison data may still be catching up.'
      } finally {
        this.loading = false
      }
    },

    hasSentiment(score) {
      return toFiniteNumber(score) !== null
    },

    formatPublished(value) {
      if (!value) return ''

      const publishedAt = new Date(value).getTime()
      if (!Number.isFinite(publishedAt)) return ''

      const diffMinutes = Math.floor((Date.now() - publishedAt) / 60000)
      if (diffMinutes < 1) return 'just now'
      if (diffMinutes < 60) return `${diffMinutes}m ago`

      const diffHours = Math.floor(diffMinutes / 60)
      if (diffHours < 24) return `${diffHours}h ago`

      const diffDays = Math.floor(diffHours / 24)
      return `${diffDays}d ago`
    },

    handleArticleClick(event, article) {
      if (!article?.urlPath) {
        event.preventDefault()
        return
      }
      event.preventDefault()
      this.navigateTo(article.urlPath)
    },

    navigateTo(path) {
      if (!path) return
      this.$emit('close')
      if (this.$route.path !== path) {
        this.$router.push({ path })
      }
    },

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.$emit('close')
        return
      }
      if (event.key === 'Tab') {
        const focusable = this.$el.querySelectorAll(
          'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
        const focusableArray = Array.from(focusable)
        const first = focusableArray[0]
        const last = focusableArray[focusableArray.length - 1]
        if (event.shiftKey) {
          if (document.activeElement === first) {
            event.preventDefault()
            last.focus()
          }
        } else {
          if (document.activeElement === last) {
            event.preventDefault()
            first.focus()
          }
        }
      }
    },

    focusFirstElement() {
      this.$nextTick(() => {
        const focusable = this.$el.querySelector('button, a[href], [tabindex]:not([tabindex="-1"])')
        if (focusable) focusable.focus()
      })
    },
  },
}
</script>

<style scoped>
.ccm {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: grid;
  place-items: center;
  padding: max(18px, env(safe-area-inset-top)) 18px max(18px, env(safe-area-inset-bottom));
}

.ccm__backdrop {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at top, rgba(59, 130, 246, 0.2), transparent 34%),
    rgba(2, 6, 23, 0.68);
  backdrop-filter: blur(12px);
}

.ccm__panel {
  position: relative;
  z-index: 1;
  display: flex;
  overflow: hidden;
  flex-direction: column;
  width: min(100%, 980px);
  max-height: min(92vh, 920px);
  border: 1px solid rgba(255, 255, 255, 0.42);
  border-radius: 30px;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.96)),
    #ffffff;
  box-shadow: 0 34px 120px rgba(2, 6, 23, 0.36);
}

.ccm__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  padding: 24px 26px 18px;
  background:
    radial-gradient(circle at top right, rgba(220, 38, 38, 0.1), transparent 36%),
    radial-gradient(circle at top left, rgba(37, 99, 235, 0.11), transparent 34%),
    #ffffff;
}

.ccm__kicker,
.ccm__claim-label {
  display: block;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.11em;
  line-height: 1;
  text-transform: uppercase;
}

.ccm__kicker i,
.ccm__claim-label i {
  margin-right: 5px;
  opacity: 0.65;
}

.ccm__header-copy {
  min-width: 0;
}

.ccm__title {
  margin: 8px 0 0;
  color: #0f172a;
  font-size: clamp(1.55rem, 4vw, 2.55rem);
  font-weight: 950;
  letter-spacing: -0.06em;
  line-height: 0.98;
}

.ccm__subtitle {
  margin: 10px 0 0;
  color: #475569;
  font-size: 0.98rem;
  font-weight: 750;
  line-height: 1.4;
}

.ccm__close {
  display: inline-grid;
  flex: 0 0 auto;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.88);
  color: #0f172a;
  cursor: pointer;
  place-items: center;
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.ccm__close:hover {
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.13);
  transform: translateY(-1px);
}

.ccm__body,
.ccm__state {
  overflow-y: auto;
  padding: 22px 26px;
}

.ccm__state {
  display: grid;
  min-height: 360px;
  align-content: center;
  justify-items: center;
  text-align: center;
}

.ccm__state p {
  max-width: 34rem;
  margin: 14px auto 0;
  color: #64748b;
  font-weight: 750;
  line-height: 1.5;
}

.ccm__loading-hint {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  margin: 18px auto 0;
  color: #64748b;
  font-size: 0.92rem;
  font-weight: 750;
}

.ccm__state h3 {
  margin: 12px 0 0;
  color: #0f172a;
  font-size: 1.45rem;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.ccm__state-icon {
  display: grid;
  width: 58px;
  height: 58px;
  border-radius: 18px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 1.55rem;
  font-weight: 950;
  place-items: center;
}

.ccm__skeleton,
.ccm__skeleton--title {
  overflow: hidden;
  border-radius: 999px;
  background: linear-gradient(90deg, #e5e7eb 0%, #f8fafc 45%, #e5e7eb 100%);
  background-size: 220% 100%;
  animation: ccm-shimmer 1.2s ease-in-out infinite;
}

.ccm__skeleton--title {
  width: min(420px, 72vw);
  height: 28px;
}

.ccm__skeleton-grid {
  display: grid;
  width: min(620px, 80vw);
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 22px;
}

.ccm__skeleton-grid .ccm__skeleton {
  height: 96px;
  border-radius: 22px;
}

@keyframes ccm-shimmer {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -120% 0;
  }
}

.ccm__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 26px;
  padding: 18px;
  background:
    radial-gradient(circle at top right, rgba(37, 99, 235, 0.08), transparent 34%),
    #ffffff;
  box-shadow: 0 18px 44px rgba(15, 23, 42, 0.07);
}

.ccm__hero--singleton {
  background:
    radial-gradient(circle at top right, rgba(100, 116, 139, 0.08), transparent 34%),
    #ffffff;
}

.ccm__hero-copy {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 16px;
}

.ccm__hero-stat {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.ccm__metric {
  display: grid;
  width: 76px;
  height: 76px;
  border-radius: 24px;
  background: #0f172a;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 950;
  letter-spacing: -0.08em;
  place-items: center;
  box-shadow: 0 20px 42px rgba(15, 23, 42, 0.2);
}

.ccm__metric-label {
  color: #64748b;
  font-size: 0.65rem;
  font-weight: 900;
  letter-spacing: 0.1em;
  line-height: 1;
  text-transform: uppercase;
}

.ccm__hero h3,
.ccm__coverage h3,
.ccm__empty h3 {
  margin: 0;
  color: #0f172a;
  font-size: 1.28rem;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.ccm__hero p,
.ccm__empty p {
  margin: 7px 0 0;
  color: #475569;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.48;
}

.ccm__hero-gap {
  flex: 0 0 auto;
}

.ccm__intelligence {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.72fr);
  gap: 16px;
  margin-top: 16px;
}

.ccm__claim,
.ccm__empty {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 24px;
  padding: 18px;
  background: #ffffff;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.ccm__claim {
  grid-column: 1 / -1;
  background:
    linear-gradient(135deg, rgba(37, 99, 235, 0.08), rgba(220, 38, 38, 0.08)),
    #ffffff;
}

.ccm__claim p {
  margin: 8px 0 0;
  color: #111827;
  font-size: clamp(1.05rem, 2.5vw, 1.35rem);
  font-weight: 900;
  letter-spacing: -0.035em;
  line-height: 1.22;
}

.ccm__claim-explainer {
  margin: 12px 0 0;
  padding: 10px 14px;
  border-left: 3px solid rgba(37, 99, 235, 0.35);
  background: rgba(248, 250, 252, 0.72);
  color: #475569;
  font-size: 0.86rem;
  font-weight: 700;
  line-height: 1.5;
}

.ccm__pulse {
  margin-top: 16px;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 24px;
  padding: 18px;
  background:
    radial-gradient(circle at top right, rgba(15, 23, 42, 0.055), transparent 40%),
    #ffffff;
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.06);
}

.ccm__pulse-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.ccm__pulse-header h3 {
  margin: 6px 0 0;
  color: #0f172a;
  font-size: 1.18rem;
  font-weight: 950;
  letter-spacing: -0.04em;
}

.ccm__pulse-score {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 10px;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 18px;
  padding: 10px 14px;
  background: #f8fafc;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.06);
}

.ccm__pulse-emoji {
  font-size: 1.75rem;
  line-height: 1;
}

.ccm__pulse-value {
  display: block;
  color: #0f172a;
  font-size: 1.3rem;
  font-weight: 950;
  letter-spacing: -0.06em;
  line-height: 1;
}

.ccm__pulse-label {
  display: block;
  margin-top: 3px;
  color: #64748b;
  font-size: 0.69rem;
  font-weight: 900;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
}

.ccm__pulse-narrative {
  margin: 12px 0 0;
  color: #111827;
  font-size: clamp(0.98rem, 2.2vw, 1.15rem);
  font-weight: 900;
  letter-spacing: -0.025em;
  line-height: 1.3;
}

.ccm__empty {
  margin-top: 16px;
}

.ccm__empty {
  display: grid;
  justify-items: center;
  min-height: 270px;
  align-content: center;
  text-align: center;
}

.ccm__empty-icon {
  display: grid;
  width: 64px;
  height: 64px;
  border-radius: 22px;
  margin-bottom: 14px;
  background: #f1f5f9;
  color: #475569;
  font-size: 1.4rem;
  place-items: center;
}

.ccm__empty-title {
  max-width: 40rem;
  border-radius: 16px;
  padding: 10px 12px;
  background: #f8fafc;
  color: #0f172a !important;
}

.ccm__coverage {
  margin-top: 18px;
}

.ccm__section-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
}

.ccm__count {
  flex: 0 0 auto;
  border-radius: 999px;
  padding: 8px 10px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.8rem;
  font-weight: 900;
}

.ccm__article-list {
  display: grid;
  gap: 11px;
}

.ccm__article {
  border: 1px solid rgba(148, 163, 184, 0.18);
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.05);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.ccm__article:hover {
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.1);
  transform: translateY(-1px);
}

.ccm__article-link {
  display: grid;
  grid-template-columns: 92px minmax(0, 1fr);
  gap: 13px;
  padding: 12px;
  color: inherit;
  text-decoration: none;
}

.ccm__article-image {
  width: 92px;
  height: 78px;
  border-radius: 16px;
  object-fit: cover;
  background: #f1f5f9;
}

.ccm__article-image--empty {
  display: grid;
  color: #64748b;
  font-size: 1.2rem;
  place-items: center;
}

.ccm__article-copy {
  min-width: 0;
}

.ccm__article-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  color: #64748b;
  font-size: 0.73rem;
  font-weight: 850;
}

.ccm__article h4 {
  display: -webkit-box;
  overflow: hidden;
  margin: 5px 0 0;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: #0f172a;
  font-size: 0.98rem;
  font-weight: 900;
  letter-spacing: -0.02em;
  line-height: 1.18;
}

.ccm__article-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 9px;
}

.ccm__tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 5px 8px;
  background: #f1f5f9;
  color: #334155;
  font-size: 0.72rem;
  font-weight: 850;
}

.ccm__tag--sentiment {
  background: #fff7ed;
  color: #9a3412;
}

.ccm__tag--source {
  background: #eef2ff;
  color: #3730a3;
}

.ccm__tag--framing {
  background: #f8fafc;
  color: #334155;
  border: 1px solid rgba(51, 65, 85, 0.14);
}

.ccm__tag--insufficient {
  background: #f3f4f6;
  color: #4b5563;
  border: 1px solid rgba(75, 85, 99, 0.2);
}

.ccm__tag--framing:focus {
  outline: 2px solid #178aef;
  outline-offset: 2px;
}

.ccm__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
  padding: 14px 26px;
  background: rgba(248, 250, 252, 0.9);
}

.ccm__footer-brand {
  margin: 0;
  color: #94a3b8;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.01em;
  white-space: nowrap;
}

.ccm__footer-brand i {
  margin-right: 5px;
  opacity: 0.7;
}

.ccm__footer-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 10px;
}

.ccm__button--primary i {
  margin-left: 6px;
}

.ccm__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  padding: 11px 15px;
  font-size: 0.88rem;
  font-weight: 900;
  text-decoration: none;
  cursor: pointer;
}

.ccm__button--primary {
  background: #0f172a;
  color: #ffffff;
  box-shadow: 0 14px 26px rgba(15, 23, 42, 0.18);
}

.ccm__button--ghost {
  background: #e2e8f0;
  color: #0f172a;
}

.ccm__button--share {
  background: #000000;
  color: #ffffff;
  gap: 7px;
}

.ccm__button--share:hover {
  background: #1a1a1a;
}

.ccm__x-logo {
  font-size: 1rem;
  font-weight: 900;
  line-height: 1;
  font-style: normal;
}

.ccm__button--upsell {
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  box-shadow: 0 14px 26px rgba(37, 99, 235, 0.22);
}

.ccm__upsell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid rgba(37, 99, 235, 0.18);
  border-radius: 22px;
  margin-top: 16px;
  padding: 16px 18px;
  background: linear-gradient(135deg, rgba(239, 246, 255, 0.9), rgba(238, 242, 255, 0.9));
}

.ccm__upsell p {
  margin: 0;
  color: #1e40af;
  font-size: 0.92rem;
  font-weight: 750;
  line-height: 1.4;
}

.ccm__upsell strong {
  font-weight: 950;
}

.ccm__opposite-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  color: #2563eb;
  font-size: 0.88rem;
  font-weight: 900;
  text-decoration: none;
  transition: gap 160ms ease;
}

.ccm__opposite-cta:hover {
  gap: 11px;
}

@media (max-width: 780px) {
  .ccm {
    padding: 0;
    place-items: stretch;
  }

  .ccm__panel {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .ccm__header,
  .ccm__body,
  .ccm__state,
  .ccm__footer {
    padding-right: 16px;
    padding-left: 16px;
  }

  .ccm__hero,
  .ccm__hero-copy,
  .ccm__section-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .ccm__intelligence {
    grid-template-columns: 1fr;
  }

  .ccm__hero-gap {
    width: 100%;
  }

  .ccm__skeleton-grid {
    grid-template-columns: 1fr;
  }

  .ccm__article-link {
    grid-template-columns: 78px minmax(0, 1fr);
  }

  .ccm__article-image {
    width: 78px;
    height: 74px;
  }

  .ccm__footer {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .ccm__footer-brand {
    display: none;
  }

  .ccm__footer-actions {
    flex-direction: column-reverse;
    width: 100%;
  }

  .ccm__footer-actions .ccm__button {
    width: 100%;
    justify-content: center;
  }

  .ccm__upsell {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
